import type { ModuleManifest, ReminderItem } from '@nexus/core';
import { MenuListItem, StatusBadge } from '@nexus/ui';
import { useTranslation } from 'react-i18next';
import { formatDate, localIsoDate } from '../i18n/format';
import { useModules } from '../modules/context';
import { dueDate } from './api';

/** Icon and module name of the module a reminder's entity belongs to. */
export function useReminderOwner(): (entity: string) => {
  icon?: ModuleManifest['icon'];
  name?: string;
} {
  const modules = useModules();
  return (entity) => {
    const module = modules.find((m) =>
      (m.manifest.activity ?? []).some((a) => a.entity === entity),
    );
    return { icon: module?.manifest.icon, name: module?.manifest.name };
  };
}

/** One open reminder as a link to its record, due day and overdue mark. */
export function ReminderEntry({ item }: { item: ReminderItem }) {
  const { t, i18n } = useTranslation();
  const owner = useReminderOwner()(item.entity);
  const overdue = item.dueOn < localIsoDate(new Date());
  return (
    <MenuListItem
      icon={owner.icon}
      href={item.path}
      wrapDescription
      description={
        <>
          {owner.name && `${t(owner.name)} · `}
          <time dateTime={item.dueOn}>{formatDate(dueDate(item.dueOn), i18n.language)}</time>
          {item.note && ` · ${item.note}`}
        </>
      }
      trailing={overdue && <StatusBadge status="danger">{t('reminders.overdue')}</StatusBadge>}
    >
      {item.title ?? t('reminders.untitled')}
    </MenuListItem>
  );
}
